import React, {useEffect, useState} from 'react';
import {useAuth} from '../../hooks/useAuth';
import {useUser} from '../../hooks/useUser';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faUserCircle, faUsers, faCrown} from '@fortawesome/free-solid-svg-icons';
import {getEventSelection, saveEventSelection} from '../../utils/eventSelections';
import { useTranslation } from 'react-i18next';

interface UserSelectorProps {
    participants: { [userId: string]: string };
    selectedParticipant: string;
    onSelectParticipant: (participant: string) => void;
    eventId?: string;
    ownerId?: string;
}

const UserSelector: React.FC<UserSelectorProps> = ({
                                                       participants,
                                                       selectedParticipant,
                                                       onSelectParticipant,
                                                       eventId,
                                                       ownerId
                                                   }) => {
    const {user} = useAuth();
    const {userProfile, getEventSelection: getUserEventSelection} = useUser();
    const [hasCheckedRemembered, setHasCheckedRemembered] = useState(false);
    const { t } = useTranslation();

    const sortedParticipantIds = Object.keys(participants).sort((a, b) => {
        // Owner luôn lên đầu
        if (a === ownerId) return -1;
        if (b === ownerId) return 1;

        // Các participant khác giữ nguyên thứ tự
        return 0;
    });

    // Auto-select remembered user when component mounts
    useEffect(() => {
        if (!eventId || hasCheckedRemembered || selectedParticipant) return;

        let rememberedSelection: string | null = null;

        if (user && userProfile) {
            // Logged in user - check saved selection from user profile
            rememberedSelection = getUserEventSelection(user.uid, eventId);
        } else {
            // Anonymous user - check localStorage
            rememberedSelection = getEventSelection(eventId);
        }

        if (rememberedSelection) {
            // Check if remembered selection is still valid participant
            if (sortedParticipantIds.includes(rememberedSelection)) {
                onSelectParticipant(rememberedSelection);
            }
        }

        setHasCheckedRemembered(true);
    }, [eventId, user, userProfile, sortedParticipantIds, selectedParticipant, hasCheckedRemembered, getUserEventSelection, onSelectParticipant]);

    const handleSelectParticipant = (participantId: string) => {
        if (!eventId) {
            console.error(t('event.userSelector.error'));
            return;
        }

        saveEventSelection(eventId, participantId);

        // Call parent callback
        onSelectParticipant(participantId);
    };

    return (
        <div className="mb-8">
            <div
                className="glass-card p-6">
                <div className="text-center mb-6">
                    <div
                        className="inline-flex items-center justify-center w-16 h-16 glass-sub-panel rounded-full mb-4">
                        <FontAwesomeIcon icon={faUsers} className="text-2xl text-blue-600 dark:text-blue-400"/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('event.userSelector.title')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        {t('event.userSelector.subtitle')}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sortedParticipantIds.map((participantId) => {
                        const participantName = participants[participantId];
                        const isOwner = participantId === ownerId;

                        return (
                            <button
                                key={participantId}
                                onClick={() => handleSelectParticipant(participantId)}
                                className={`relative flex items-center p-4 rounded-lg border-2 border-transparent transition-all group ${
                                    isOwner
                                        ? 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 hover:border-amber-300 dark:hover:border-amber-600 hover:bg-gradient-to-r hover:from-amber-100 hover:to-yellow-100 dark:hover:from-amber-800/30 dark:hover:to-yellow-800/30'
                                        : 'bg-gray-50 dark:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                                }`}
                            >
                                {/* Owner Crown Badge */}
                                {isOwner && (
                                    <div
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 dark:bg-amber-600 rounded-full flex items-center justify-center shadow-sm">
                                        <FontAwesomeIcon
                                            icon={faCrown}
                                            className="text-xs text-white"
                                        />
                                    </div>
                                )}

                                <div
                                    className={`flex items-center justify-center w-10 h-10 rounded-full mr-3 transition-colors ${
                                        isOwner
                                            ? 'bg-amber-100 dark:bg-amber-900/30 group-hover:bg-amber-200 dark:group-hover:bg-amber-800/50'
                                            : 'bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50'
                                    }`}>
                                    <FontAwesomeIcon
                                        icon={faUserCircle}
                                        className={`text-lg ${
                                            isOwner
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-blue-600 dark:text-blue-400'
                                        }`}
                                    />
                                </div>

                                <div className="flex-1 text-left">
                                    <div
                                        className={`font-medium ${
                                            isOwner
                                                ? 'text-amber-900 dark:text-amber-100'
                                                : 'text-gray-900 dark:text-white'
                                        }`}>
                                        {participantName}
                                    </div>
                                    {isOwner && (
                                        <div className="text-xs text-amber-600 dark:text-amber-400">
                                            {t('event.userSelector.owner')}
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default UserSelector;